// ============================================================
// TtrGameEndModal — 게임 종료 결과 모달
//   SoT: H:\AI\AI_Setting\보드겜돌이\티켓투라이드.md §2-7 / §4-2 TtrGameEnded
//   SEQ=10146 — 점수 breakdown (routeScore/ticketScore/longestBonus/total)
//                + 전원 티켓 공개 (종료 시 비대칭 해제, SoT §8)
//                + WINNER 강조 (동률 시 공동 우승)
// ============================================================
import React, { useMemo } from 'react'
import { TtrGameEndedPayload, TtrPlayer, TtrCity } from '../store/ticket'

export interface TtrGameEndModalProps {
  endPayload: TtrGameEndedPayload
  players:    TtrPlayer[]
  cities:     TtrCity[]
  onClose:    () => void
}

const TtrGameEndModal: React.FC<TtrGameEndModalProps> = ({
  endPayload, players, cities, onClose,
}) => {
  // total 내림차순 + 동률은 winnerUserCode/longestPathHolders 기준
  const sorted = useMemo(() => {
    return [...endPayload.scoreBreakdown].sort((a, b) => b.total - a.total)
  }, [endPayload])

  const cityName = useMemo(() => {
    const map = new Map<number, string>()
    cities.forEach(c => map.set(c.cityId, c.name))
    return map
  }, [cities])

  function nicknameOf(uc: number): string {
    return players.find(pl => pl.userCode === uc)?.nickname ?? `#${uc}`
  }

  // 동률 우승: winnerUserCode == null 시 최고 점수 공동
  const topScore = sorted.length > 0 ? sorted[0].total : 0
  const winners = sorted.filter(s => s.total === topScore).map(s => s.userCode)

  return (
    <div className="ttr-modal-overlay">
      <div className="ttr-modal ttr-game-end-modal">
        <h2>게임 종료</h2>

        <div className="ttr-winner-banner">
          {winners.length === 1
            ? <>WINNER: {nicknameOf(winners[0])}</>
            : <>공동 우승: {winners.map(nicknameOf).join(' · ')}</>}
        </div>

        <table className="ttr-score-table">
          <thead>
            <tr>
              <th>순위</th>
              <th>플레이어</th>
              <th>노선</th>
              <th>티켓 ±</th>
              <th>최장경로</th>
              <th>총점</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((s, idx) => {
              const isWinner = winners.includes(s.userCode)
              const isLongest = endPayload.longestPathHolders.includes(s.userCode)
              return (
                <tr key={s.userCode} className={isWinner ? 'winner-row' : ''}>
                  <td>{idx + 1}</td>
                  <td>
                    {nicknameOf(s.userCode)}
                    {isLongest && <span className="ttr-longest-badge"> 최장경로</span>}
                  </td>
                  <td>{s.routeScore}</td>
                  <td>{s.ticketScore >= 0 ? '+' + s.ticketScore : s.ticketScore}</td>
                  <td>{s.longestBonus > 0 ? '+' + s.longestBonus : '-'}</td>
                  <td><strong>{s.total}</strong></td>
                </tr>
              )
            })}
          </tbody>
        </table>

        {/* 전원 티켓 공개 (SoT §8 — 종료 시 비대칭 해제) */}
        <div className="ttr-all-tickets-reveal">
          <h3>전원 티켓 공개</h3>
          {endPayload.allTickets.map(group => (
            <div key={group.userCode} className="ttr-player-tickets">
              <h4>{nicknameOf(group.userCode)} ({group.tickets.length}장)</h4>
              <ul>
                {group.tickets.map(t => {
                  const a = cityName.get(t.cityAId) ?? `#${t.cityAId}`
                  const b = cityName.get(t.cityBId) ?? `#${t.cityBId}`
                  return (
                    <li key={t.ticketId} className={t.isCompleteUx ? 'complete' : 'incomplete'}>
                      {a} ↔ {b} ({t.points}점)
                      {t.isCompleteUx ? ' OK' : ' X'}
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </div>

        <div className="ttr-modal-actions">
          <button onClick={onClose}>로비로</button>
        </div>
      </div>
    </div>
  )
}

export default TtrGameEndModal
